import { useEffect, useId, useRef } from "react";
import { X } from "lucide-react";

const RULES: { title: string; body: string }[] = [
	{
		title: "Staked MMK only votes",
		body: "Vote weight comes from staked MMK. Wallet MMK that is not staked does not vote.",
	},
	{
		title: "24h cliff + 180d vest",
		body: "New stake has a 24-hour cliff, then vote weight unlocks linearly over 180 days — blocking flash stake-and-vote games.",
	},
	{
		title: "~4.9% ownership / vote cap",
		body: "No single principal can hold more than ~4.9% of MMK for buy or vote-weight purposes.",
	},
	{
		title: "Param quorum",
		body: "Parameter proposals need max(absolute floor, 10% of unlocked vote weight) to tally.",
	},
	{
		title: "3-day voting period",
		body: "Proposals stay active for three days before tally. They are not auto-passed on create.",
	},
	{
		title: "24h execution timelock",
		body: "After a pass, execution waits 24 hours so the community can react before parameters change.",
	},
	{
		title: "Hard parameter bounds",
		body: "Create and execute validate fees, CR, ceilings, and pauses against on-chain ranges — the DAO cannot set arbitrary values.",
	},
	{
		title: "1,000 MMK to create param proposals",
		body: "Non-admin creators need at least 1,000 staked MMK to open parameter proposals.",
	},
	{
		title: "Controllers stay outside the DAO",
		body: "Wasm upgrades and controller changes are not DAO actions. Protocol-held MMK is non-voting.",
	},
	{
		title: "Official IC assets SPA only",
		body: "Stake and vote only from this app on the official Internet Computer assets URL. Never approve MMK from DMs or look-alike sites.",
	},
];

type Props = {
	open: boolean;
	onClose: () => void;
};

export function DaoRulesDialog({ open, onClose }: Props) {
	const titleId = useId();
	const closeRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		if (!open) return;
		closeRef.current?.focus();
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [open, onClose]);

	if (!open) return null;

	return (
		<div
			className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 sm:items-center"
			role="presentation"
			onClick={onClose}
		>
			<div
				role="dialog"
				aria-modal="true"
				aria-labelledby={titleId}
				className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-border bg-background p-5 shadow-xl"
				onClick={(e) => e.stopPropagation()}
			>
				<div className="flex items-start justify-between gap-3">
					<div>
						<p className="section-eyebrow">Governance</p>
						<h2 id={titleId} className="text-lg font-bold">
							10 key DAO rules
						</h2>
					</div>
					<button
						ref={closeRef}
						type="button"
						className="rounded-xl border border-border p-2"
						aria-label="Close"
						onClick={onClose}
					>
						<X className="size-4" />
					</button>
				</div>

				<ol className="mt-4 space-y-3">
					{RULES.map((r, i) => (
						<li key={r.title} className="rounded-xl border border-border/60 bg-muted/30 p-3">
							<p className="text-sm font-bold">
								{i + 1}. {r.title}
							</p>
							<p className="mt-1 text-xs text-muted-foreground">{r.body}</p>
						</li>
					))}
				</ol>

				<section className="mt-5 rounded-xl border border-warning/40 bg-warning/10 p-4">
					<p className="text-xs font-bold uppercase tracking-wide text-warning">
						51% voting attack protection
					</p>
					<p className="mt-2 text-sm text-muted-foreground">
						Buying a majority of liquid MMK does not instantly capture governance.
						Vote weight requires staking through the 24h cliff and 180-day vest,
						is capped near 4.9% per principal, and must still clear quorum over a
						3-day vote plus a 24h execution timelock. Flash-bought stake has no
						immediate vote weight.
					</p>
				</section>
			</div>
		</div>
	);
}
